import { Dispatch } from 'redux';
import gql from '../../services/gql';
import { State } from '../reducers';
import { CacheKey, CacheExpiration, cache, retrieve } from '../../services/cache';
import {
  setConfigurationsLoadingAction,
  setConfigurationsLoadedAction,
  setCurrentRecruitingYearAction,
  setNFLDraftYearAction,
  setNewHSPlayersDaysAction,
  setNewCollegePlayersDaysAction,
  setDashboardRolloverDayAction,
  setDashboardRolloverMonthAction,
  setRolloverDayAction,
  setRolloverMonthAction,
  setTrackDatePastYearLimitAction,
  setRolloverDateTransferPortal,
  setRolloverMonthTransferPortal,
  setRolloverDayNflAction,
  setRolloverMonthNflAction,
  setFbsProfilerMinYearAction,
  setFcsProfilerMinYearAction,
  setQbProfilerMinPassingAttemptAction,
  setRusherProfilerMinRushingAttemptAction,
  setReceiverProfilerMinReceptionsAction,
  setDefenderProfilerMinDefensiveSnapsAction,
} from '../actions/configurations';
import Configuration, { CONFIGURATION_KEY } from '../../types/Configuration';

export function fetchConfigurations () {
  return (dispatch:Dispatch, getState:() => State) => {
    const { configurations: { configurationsLoading, configurationsLoaded } } = getState();

    if (configurationsLoading || configurationsLoaded) return;

    const cachedConfigurations = retrieve(CacheKey.CONFIGURATIONS);
    const cacheIsValid = !!cachedConfigurations?.length;

    if (cacheIsValid) {
      processConfigurations(cachedConfigurations, dispatch);

      if (!configurationsLoaded) {
        dispatch(setConfigurationsLoadedAction(true));
      }

      return;
    }

    dispatch(setConfigurationsLoadingAction(true));

    return gql(`
      configurations (keys: [
        "${CONFIGURATION_KEY.CURRENT_RECRUITING_YEAR}",
        "${CONFIGURATION_KEY.NFL_DRAFT_YEAR}",
        "${CONFIGURATION_KEY.NEW_HS_PLAYERS_DAYS}",
        "${CONFIGURATION_KEY.NEW_COLLEGE_PLAYERS_DAYS}",
        "${CONFIGURATION_KEY.DASHBOARD_ROLLOVER_DAY}",
        "${CONFIGURATION_KEY.DASHBOARD_ROLLOVER_MONTH}",
        "${CONFIGURATION_KEY.ROLLOVER_DAY}",
        "${CONFIGURATION_KEY.ROLLOVER_MONTH}",
        "${CONFIGURATION_KEY.ROLLOVER_DAY_NFL}",
        "${CONFIGURATION_KEY.ROLLOVER_MONTH_NFL}",
        "${CONFIGURATION_KEY.TRACK_DATE_PAST_YEAR_LIMIT}",
        "${CONFIGURATION_KEY.ROLLOVER_DATE_TRANSFER_PORTAL}",
        "${CONFIGURATION_KEY.ROLLOVER_MONTH_TRANSFER_PORTAL}",
        "${CONFIGURATION_KEY.FBS_PROFILER_MIN_YEAR}",
        "${CONFIGURATION_KEY.FCS_PROFILER_MIN_YEAR}",
        "${CONFIGURATION_KEY.QB_PROFILER_MIN_PASSING_ATTEMPT}",
        "${CONFIGURATION_KEY.RUSHER_PROFILER_MIN_RUSHING_ATTEMPT}",
        "${CONFIGURATION_KEY.RECEIVER_PROFILER_MIN_RECEPTIONS}",
        "${CONFIGURATION_KEY.DEFENDER_PROFILER_MIN_DEFENSIVE_SNAPS}",
      ]) {
        key
        value
      }
    `)
      .then((data:any) => data.configurations as Configuration[])
      .then((configurations:Configuration[]) => {
        processConfigurations(configurations, dispatch);
        cache(CacheKey.CONFIGURATIONS, configurations, CacheExpiration.LONG);
      })
      .catch(console.error)
      .finally(() => {
        dispatch(setConfigurationsLoadingAction(false));
        dispatch(setConfigurationsLoadedAction(true));
      });
  };
}

function processConfigurations (configurations:Configuration[], dispatch:Dispatch) {
  if (configurations && configurations.length) {
    const currentRecruitingYear = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.CURRENT_RECRUITING_YEAR) || {} as any)
      .value;
    if (currentRecruitingYear) {
      dispatch(setCurrentRecruitingYearAction(Number(currentRecruitingYear)));
    }

    const nflDraftYear = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.NFL_DRAFT_YEAR) || {} as any)
      .value;
    if (nflDraftYear) {
      dispatch(setNFLDraftYearAction(Number(nflDraftYear)));
    }

    const newHSPlayersDays = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.NEW_HS_PLAYERS_DAYS) || {} as any)
      .value;
    if (newHSPlayersDays) {
      dispatch(setNewHSPlayersDaysAction(Number(newHSPlayersDays)));
    }

    const newCollegePlayersDays = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.NEW_COLLEGE_PLAYERS_DAYS) || {} as any)
      .value;
    if (newCollegePlayersDays) {
      dispatch(setNewCollegePlayersDaysAction(Number(newCollegePlayersDays)));
    }

    const dashboardRolloverDay = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.DASHBOARD_ROLLOVER_DAY) || {} as any)
      .value;
    if (dashboardRolloverDay) {
      dispatch(setDashboardRolloverDayAction(Number(dashboardRolloverDay)));
    }

    const dashboardRolloverMonth = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.DASHBOARD_ROLLOVER_MONTH) || {} as any)
      .value;
    if (dashboardRolloverMonth) {
      dispatch(setDashboardRolloverMonthAction(Number(dashboardRolloverMonth)));
    }

    const rolloverDay = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.ROLLOVER_DAY) || {} as any)
      .value;
    if (rolloverDay) {
      dispatch(setRolloverDayAction(Number(rolloverDay)));
    }

    const rolloverMonth = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.ROLLOVER_MONTH) || {} as any)
      .value;
    if (rolloverMonth) {
      dispatch(setRolloverMonthAction(Number(rolloverMonth)));
    }

    const rolloverDayNfl = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.ROLLOVER_DAY_NFL) || {} as any)
      .value;
    if (rolloverDayNfl) {
      dispatch(setRolloverDayNflAction(Number(rolloverDayNfl)));
    }

    const rolloverMonthNfl = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.ROLLOVER_MONTH_NFL) || {} as any)
      .value;
    if (rolloverMonthNfl) {
      dispatch(setRolloverMonthNflAction(Number(rolloverMonthNfl)));
    }

    const trackDatePastYearLimit = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.TRACK_DATE_PAST_YEAR_LIMIT) || {} as any)
      .value;
    if (trackDatePastYearLimit) {
      dispatch(setTrackDatePastYearLimitAction(Number(trackDatePastYearLimit)));
    }

    const rolloverDateTransferPortal = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.ROLLOVER_DATE_TRANSFER_PORTAL) || {} as any)
      .value;
    if (rolloverDateTransferPortal) {
      dispatch(setRolloverDateTransferPortal(Number(rolloverDateTransferPortal)));
    }

    const rolloverMonthTransferPortal = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.ROLLOVER_MONTH_TRANSFER_PORTAL) || {} as any)
      .value;
    if (rolloverMonthTransferPortal) {
      dispatch(setRolloverMonthTransferPortal(Number(rolloverMonthTransferPortal)));
    }

    const fbsProfilerMinYear = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.FBS_PROFILER_MIN_YEAR) || {} as any)
      .value;
    if (fbsProfilerMinYear) {
      dispatch(setFbsProfilerMinYearAction(Number(fbsProfilerMinYear)));
    }

    const fcsProfilerMinYear = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.FCS_PROFILER_MIN_YEAR) || {} as any)
      .value;
    if (fcsProfilerMinYear) {
      dispatch(setFcsProfilerMinYearAction(Number(fcsProfilerMinYear)));
    }

    const qbProfilerMinPassingAttempt = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.QB_PROFILER_MIN_PASSING_ATTEMPT) || {} as any)
      .value;
    if (qbProfilerMinPassingAttempt) {
      dispatch(setQbProfilerMinPassingAttemptAction(Number(qbProfilerMinPassingAttempt)));
    }

    const rusherProfilerMinRushingAttempt = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.RUSHER_PROFILER_MIN_RUSHING_ATTEMPT) || {} as any)
      .value;
    if (rusherProfilerMinRushingAttempt) {
      dispatch(setRusherProfilerMinRushingAttemptAction(Number(rusherProfilerMinRushingAttempt)));
    }

    const receiverProfilerMinReceptions = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.RECEIVER_PROFILER_MIN_RECEPTIONS) || {} as any)
      .value;
    if (receiverProfilerMinReceptions) {
      dispatch(setReceiverProfilerMinReceptionsAction(Number(receiverProfilerMinReceptions)));
    }

    const defenderProfilerMinDefensiveSnaps = (configurations
      .find(configuration => configuration.key === CONFIGURATION_KEY.DEFENDER_PROFILER_MIN_DEFENSIVE_SNAPS) || {} as any)
      .value;
    if (defenderProfilerMinDefensiveSnaps) {
      dispatch(setDefenderProfilerMinDefensiveSnapsAction(Number(defenderProfilerMinDefensiveSnaps)));
    }
  }
}